import { useCallback, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Box, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { Textarea } from "app/pages/sales-intelligence/common-components/Textarea/Textarea";
import { useGetUserInfo } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetUserInfo";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { MAX_COMPETITORS } from "app/pages/sales-intelligence/pages/ai-agent/components/CompetitorsAutocomplete/CompetitorsAutocomplete";
import { FormItem } from "app/pages/sales-intelligence/pages/ai-agent/components/FormItem/FormItem";
import { MeetingGoal } from "app/pages/sales-intelligence/pages/ai-agent/components/MeetingGoal/MeetingGoal";
import { Attendees } from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/Attendees";
import { MeetingCompetitors } from "app/pages/sales-intelligence/pages/ai-agent/containers/MeetingCompetitors/MeetingCompetitors";
import type { FirstRespondFormType } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useFirstRespondForm";
import { useThreadCreatedDate } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useThreadCreatedDate";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";

import { TypingText } from "../../../../modules/ai-agent/components/TypingText/TypingText";

const RELEASE_DATE = "2025-07-17";
const TOTAL_STEPS = 4;

const FirstRespondFormContent = ({
    isDisabled,
    form,
    defaultCompetitors,
}: {
    isDisabled: boolean;
    form: FirstRespondFormType;
    defaultCompetitors: Competitor[];
}) => {
    const {
        params: { id, domain },
    } = useCurrentStateAndParams<{ id?: string; domain?: string }>();
    const [visibleStep, setVisibleStep] = useState(isDisabled ? TOTAL_STEPS : 0);

    const { data: userInfo, isLoading } = useGetUserInfo();

    const { isContactsVisible, isLoadingContactStatus } = useIsContactsAvailable();
    const threadCreatedDate = useThreadCreatedDate(id);

    const isNewThread = threadCreatedDate ? new Date(threadCreatedDate) >= new Date(RELEASE_DATE) : true;

    const handleSetNextStep = useCallback(() => {
        setVisibleStep((prev) => {
            if (prev < TOTAL_STEPS) {
                return prev + 1;
            } else {
                return prev;
            }
        });
    }, []);

    if (isLoading) {
        return null;
    }

    return (
        <Box padding={isDisabled ? 0 : "0 0 200px"}>
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon[500]}
                data-automation="ai-agent-welcome-text"
            >
                <TypingText
                    text={translate("si.pages.ai_agent.form.heading", {
                        companyName: userInfo?.companyName,
                    })}
                    onDone={handleSetNextStep}
                    isDisabledTyping={isDisabled}
                />
            </Text>

            {visibleStep > 0 ? (
                <Flex
                    flexDirection="column"
                    gap="28px"
                    marginY={"24px 0"}
                >
                    <FormItem
                        title={translate("si.pages.ai_agent.form.goal")}
                        onDone={handleSetNextStep}
                        isDisabledTyping={isDisabled}
                        dataAutomation="ai-agent-meeting-goal"
                    >
                        <MeetingGoal
                            valueOption={form.values.meetingGoalOption}
                            disabled={isDisabled}
                            onChange={form.onUpdate}
                            value={form.values.meetingGoal}
                        />
                    </FormItem>

                    {visibleStep > 1 ? (
                        <FormItem
                            title={translate("si.pages.ai_agent.form.keyAttendees")}
                            onDone={handleSetNextStep}
                            isDisabledTyping={isDisabled}
                            dataAutomation={`ai-agent-attendees`}
                        >
                            {(isContactsVisible || isLoadingContactStatus) && isNewThread ? (
                                <Attendees
                                    value={form.values.attendeesContacts ?? []}
                                    onChange={(value) => form.onUpdate("attendeesContacts", value)}
                                    disabled={isDisabled}
                                />
                            ) : (
                                <Textarea
                                    value={form.values.attendees}
                                    onChange={(value) => form.onUpdate("attendees", value)}
                                    onPaste={() => AIAgentTrackingService.meeting.trackInsertText("attendees")}
                                    disabled={isDisabled}
                                    placeholder={translate("si.pages.ai_agent.form.keyAttendees.placeholder")}
                                />
                            )}
                        </FormItem>
                    ) : null}

                    {visibleStep > 2 ? (
                        <FormItem
                            title={`${translate("si.pages.ai_agent.form.competitors")} (${form.values.competitors?.length ?? 0}/${MAX_COMPETITORS})`}
                            isDisabledTyping={isDisabled}
                            onDone={handleSetNextStep}
                            dataAutomation="ai-agent-competitors"
                        >
                            <MeetingCompetitors
                                value={form.values.competitors || []}
                                onChange={(value) => form.onUpdate("competitors", value)}
                                disabled={isDisabled}
                                defaultCompetitors={defaultCompetitors}
                            />
                        </FormItem>
                    ) : null}

                    {visibleStep > 3 ? (
                        <FormItem
                            title={translate("si.pages.ai_agent.form.generalPoints")}
                            isDisabledTyping={isDisabled}
                            dataAutomation={`ai-agent-otherNotes`}
                        >
                            <Textarea
                                value={form.values.otherNotes}
                                onChange={(value) => form.onUpdate("otherNotes", value)}
                                onPaste={() => AIAgentTrackingService.meeting.trackInsertText("other notes")}
                                disabled={isDisabled}
                                placeholder={translate("si.pages.ai_agent.form.generalPoints.placeholder")}
                            />
                        </FormItem>
                    ) : null}
                </Flex>
            ) : null}
        </Box>
    );
};

export default FirstRespondFormContent;
